import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
// icons
import { IconContext } from "react-icons/lib"
import { IoChevronDown, IoChevronUp, IoChevronBackOutline } from "react-icons/io5"
import { useParams } from "react-router-dom"
import { FunctionComponent } from "react"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import TabPanel, { TabType } from "@murphy-frontend/web-core/components/Tabs/TabPanel"
import Range from "../components/Range"
import Actions from "../components/Actions"
import Comments from "../components/Comments"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import { useGetCustomerBlocks } from "../api/queries"
import { RequirementBlockNoteDTO, RequirementBlockActionPointDTO, CustomerBlockDto } from "../api/models"
import { RequirementCategories } from "@murphy-frontend/web-core/constants"
import { translateConstantsWithBackendTranslations } from "../../../common/services/translationService"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import Button from "@murphy-frontend/web-core/components/Button"


interface QuestionnaireTabs {
	id: number
	name: string
}

interface QuestionnaireProps { }

const Questionnaire: FunctionComponent<QuestionnaireProps> = () => {
	const [isToggled, setIsToggled] = useState(false)
	const [tabs, setTabs] = useState<TabType[]>([])
	const [selectedTabId, setSelectedTabId] = useState<number>(1)
	const [customerBlock, setCustomerBlock] = useState<CustomerBlockDto>({})
	const [actions, setActions] = useState<RequirementBlockActionPointDTO[]>([])
	const [notes, setNotes] = useState<RequirementBlockNoteDTO[]>([])
	const [nextBlock, setNextBlock] = useState(-1)
	const [prevBlock, setPrevBlock] = useState(-1)

	const navigate = useNavigate()
	let { id, customerId } = useParams()
	const { customer } = useCustomer()
	const { translations } = useUserPreferences()

	if (!customerId)
		customerId = customer.Id

	const { data: customerBlocks, isLoading: customerBlocksIsLoading } = useGetCustomerBlocks(customerId)
	const translatedRequirementCategories = translateConstantsWithBackendTranslations(
		RequirementCategories,
		translations
	)


	const tabsData: QuestionnaireTabs[] = [
		{
			id: 1,
			name: translations.commentTab || "Comments"
		},
		{
			id: 2,
			name: translations.actionTab || "Actions"
		}
	]

	const setInitialTabs = () => {
		const initialTabs: Array<TabType> = tabsData.map(lvl => {
			return {
				id: lvl.id,
				displayText: lvl.name,
				customClass: "tab-transparent-panel-tab",
				customSelectedClass: "tab-transparent-panel-tab-selected wrap-text"
			}
		})
		setTabs(initialTabs)
	}

	useEffect(() => {
		setInitialTabs()
	}, [])

	useEffect(() => {
		if (!customerBlocksIsLoading) {
			const idx = customerBlocks.findIndex(el => el.Id === parseInt(id))
			const requirement = customerBlocks[idx]

			if (idx === customerBlocks.length - 1) {
				setNextBlock(-1)
				setPrevBlock(customerBlocks[idx - 1].Id)
			} else if (idx === 0) {
				setPrevBlock(-1)
				setNextBlock(customerBlocks[idx + 1].Id)
			} else {
				setNextBlock(customerBlocks[idx + 1].Id)
				setPrevBlock(customerBlocks[idx - 1].Id)
			}

			setCustomerBlock(requirement)
			setActions(requirement.ActionPoints)
			setNotes(requirement.Notes)
		}
	}, [id, customerBlocksIsLoading, customerBlocks])

	const addCollapsedClass = (toggled: boolean, classes: string) => {
		if (toggled) return classes + "--collapsed"
		else return classes
	}

	const onClickTab = (tabId: number) => {
		setSelectedTabId(tabId)
	}

	const category = translatedRequirementCategories.find(el => el.id === customerBlock.RequirementCategoryId)

	if (customerBlocksIsLoading) {
		return (
			<ComponentWrapper active={3} subMenuActive={2}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	return (
		<ComponentWrapper active={3} subMenuActive={2}>
			<section className='header-btns'>
				<div className='header-back-btn'>
					<Button
						buttonSize={"btn--medium"}
						isLightButton={true}
						onClick={() => navigate(`/tmscategory/${customerId}/${category.id}`)}
					>
						<IconContext.Provider value={{ className: "btn-icon" }}>
							<IoChevronBackOutline />
						</IconContext.Provider>
						&nbsp;	{translations.backTo || "Back to "} &nbsp;<span>{category?.value}</span>
					</Button>
				</div>
				<div className='header-right-btn'>
					<Button
						buttonSize={"btn--medium"}
						isLightButton={true}
						disabled={prevBlock === -1}
						onClick={() => navigate(`/questionnaire/${customerId}/${prevBlock}`)}
					>
						{translations.prevBox || "Previous"}
					</Button>
					<Button
						buttonSize={"btn--medium"}
						isLightButton={true}
						disabled={nextBlock === -1}
						onClick={() => navigate(`/questionnaire/${customerId}/${nextBlock}`)}
					>
						{translations.nextBox || "Next"}
					</Button>
				</div>
			</section>
			<section className='card-container'>
				<div className='questionnaire-header-wrapper'>
					<h4 className='questionnaire-header'>{customerBlock.Name}</h4>
					<div className='questionnaire-tags'>
						<div className='questionnaire-tag'>{`Level ${customerBlock.Level}`}</div>
						<div className='questionnaire-tag'>{category?.value}</div>
					</div>
				</div>
				<div className={addCollapsedClass(isToggled, "card-container margin-top-10 questionnaire-intro")}>
					<div className='card-container--paragraph'>{customerBlock.HtmlDescription}</div>
					<div className={"card-container--title questionnaire-title-wrap"}>
						<IconContext.Provider value={{ className: "btn-icon" }}>
							{isToggled ? (
								<IoChevronUp onClick={() => setIsToggled(false)} />
							) : (
								<IoChevronDown onClick={() => setIsToggled(true)} />
							)}
						</IconContext.Provider>
					</div>
				</div>
				<div className='card-container margin-top-10'>
					{customerBlock.RequirementOptions && (
						<Range
							key={customerBlock.Id} // Force re-mount when the id changes
							requirementOptions={customerBlock.RequirementOptions}
							categoryId={customerBlock.RequirementCategoryId}
							requirementStatus={customerBlock.RequirementStatus}
							blockId={customerBlock.Id}
						/>
					)}
				</div>
				<div className='questionnaire-tabs'>
					<TabPanel
						tabs={tabs}
						onClickTab={onClickTab}
						selectedTabId={selectedTabId}
						customClass='tab-solid-panel-tms'
					/>
				</div>
				<div className='questionnaire-tabs'>
					{selectedTabId === 1 && <Comments comments={notes} blockId={customerBlock.Id} />}
					{selectedTabId === 2 && <Actions actions={actions} blockId={customerBlock.Id} />}
				</div>
			</section>
		</ComponentWrapper>
	)
}

export default Questionnaire
